<template>
  <div ref="comboboxWrapper">
    <b-label
        v-if="props.label"
        class="block text-sm font-medium"
        :for="props.id"
    >{{ props.label }}</b-label>
    
    <!-- Searchable Combobox Implementation -->
    <Combobox v-if="searchable" :key="inputKey" v-model="selectedItem" by="value" as="div" class="relative mt-1">
      <div ref="comboboxTriggerRef" class="relative">
        <ComboboxInput
          class="h-[50px] w-full focus:ring-primary px-3 border border-[#373737] rounded-lg bg-transparent text-white placeholder-gray-400"
          @change="debouncedFilter"
          :display-value="(item) => item?.label || ''"
          :placeholder="props.placeholder"
          @click="handleInputClick"
        />
        <ComboboxButton ref="comboboxButtonRef" class="absolute inset-y-0 right-0 flex items-center pr-2">
          <Icon name="solar:alt-arrow-down-linear" size="1.2rem" class="text-gray-500" />
        </ComboboxButton>
      </div>
      <Teleport to="body">
        <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-out"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
        >
          <ComboboxOptions
              class="fixed z-[100] max-h-60 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-gray-300 ring-opacity-5 focus:outline-none"
              :style="comboboxDropdownStyle"
          >
          <template v-if="isGrouped">
            <template v-for="group in filteredItems" :key="group.label">
              <div class="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b">{{ group.label }}</div>
              <ComboboxOption v-for="item in group.options" :key="item.value" :value="item" v-slot="{ active, selected }" data-e2e="select-option">
                <li :class="['relative select-none py-2 pl-10 pr-4', active ? 'bg-blue-100 text-blue-900' : 'text-gray-900']">
                  <span :class="['block truncate', selected ? 'font-medium' : 'font-normal']">{{ item.label }}</span>
                  <span v-if="selected" class="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600"></span>
                </li>
              </ComboboxOption>
            </template>
          </template>
          <template v-else>
            <div v-if="!filteredItems || filteredItems.length === 0" class="relative cursor-default select-none py-2 px-4 text-gray-700">Nothing found.</div>
            <ComboboxOption v-for="item in filteredItems" :key="item.value" :value="item" v-slot="{ active, selected }" data-e2e="select-option">
               <li :class="['relative select-none py-2 pl-10 pr-4', active ? 'bg-blue-100 text-blue-900' : 'text-gray-900']">
                  <span :class="['block truncate', selected ? 'font-medium' : 'font-normal']">{{ item.label }}</span>
                  <span v-if="selected" class="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600"></span>
                </li>
            </ComboboxOption>
          </template>
          </ComboboxOptions>
        </transition>
      </Teleport>
    </Combobox>

    <!-- Original Listbox Implementation -->
    <Listbox v-else v-model="selectedItem" by="value" as="div" class="relative mt-1">
      <div ref="listboxTriggerRef">
        <ListboxButton class="h-[50px] w-full focus:ring-primary px-3 border border-[#373737] rounded-lg">
        <span v-if="selectedItem" class="block truncate text-gray-300 text-start">{{ selectedItem.label }}</span>
        <span v-else class="block truncate text-gray-400 text-start">{{ props.placeholder }}</span>
        <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"></span>
      </ListboxButton>
      </div>
      <Teleport to="body">
        <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-out"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
        >
          <ListboxOptions
              class="fixed z-[100] max-h-60 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-gray-300 ring-opacity-5 focus:outline-none"
              :style="listboxDropdownStyle"
          >
          <template v-if="isGrouped">
            <template v-for="group in items" :key="group.label">
              <div class="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b">{{ group.label }}</div>
              <ListboxOption v-for="item in group.options" :key="item.value" :value="item" v-slot="{ active, selected }" data-e2e="select-option">
                <li :class="['relative select-none py-2 pl-10 pr-4', active ? 'bg-blue-100 text-blue-900' : 'text-gray-900']">
                  <span :class="['block truncate', selected ? 'font-medium' : 'font-normal']">{{ item.label }}</span>
                  <span v-if="selected" class="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600"></span>
                </li>
              </ListboxOption>
            </template>
          </template>
          <template v-else>
            <ListboxOption v-for="item in items" :key="item.value" :value="item" v-slot="{ active, selected }" data-e2e="select-option">
              <li :class="['relative select-none py-2 pl-10 pr-4', active ? 'bg-blue-100 text-blue-900' : 'text-gray-900']">
                  <span :class="['block truncate', selected ? 'font-medium' : 'font-normal']">{{ item.label }}</span>
                  <span v-if="selected" class="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600"></span>
                </li>
            </ListboxOption>
          </template>
          </ListboxOptions>
        </transition>
      </Teleport>
    </Listbox>
  </div>
</template>
<script lang="ts" setup>
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from '@headlessui/vue'
import BLabel from '../BLabel.vue';
import { computed, ref, watch, nextTick } from 'vue';
import { useDebounceFn, onClickOutside, useElementBounding } from '@vueuse/core';

export interface SelectItem {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectGroup {
  label: string;
  options: SelectItem[];
}

interface Props {
  items: SelectItem[] | SelectGroup[];
  id?: string;
  label?: string;
  placeholder?: string;
  name?: string;
  modelValue: SelectItem | null;
  searchable?: boolean;
  rules?: string | Record<string, any> | Function;
  autocomplete?: Boolean;
  top?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  modelValue: null,
  id: () => `select-${new Date().getTime().toString()}`,
  searchable: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: SelectItem | null): void
}>()

const comboboxButtonRef = ref(null);
const inputKey = ref(0);

const comboboxTriggerRef = ref<HTMLElement | null>(null);
const listboxTriggerRef = ref<HTMLElement | null>(null);

const comboboxBounding = useElementBounding(comboboxTriggerRef);
const listboxBounding = useElementBounding(listboxTriggerRef);

const comboboxDropdownStyle = computed(() => {
  const { top, bottom, left, width } = comboboxBounding;
  return props.top
    ? { bottom: `${window.innerHeight - top.value + 4}px`, left: `${left.value}px`, width: `${width.value}px` }
    : { top: `${bottom.value + 4}px`, left: `${left.value}px`, width: `${width.value}px` };
});

const listboxDropdownStyle = computed(() => {
  const { top, bottom, left, width } = listboxBounding;
  return props.top
    ? { bottom: `${window.innerHeight - top.value + 4}px`, left: `${left.value}px`, width: `${width.value}px` }
    : { top: `${bottom.value + 4}px`, left: `${left.value}px`, width: `${width.value}px` };
});

// Force re-render of ComboboxInput when modelValue changes (HeadlessUI bug workaround)
watch(() => props.modelValue, () => {
  inputKey.value++;
}, { deep: true });

const handleInputClick = () => {
  if (comboboxButtonRef.value) {
    const buttonElement = comboboxButtonRef.value.$el;
    if (buttonElement) {
      buttonElement.click();
    }
  }
};

const isGrouped = computed(() => {
  return props.items.length > 0 && 'options' in props.items[0]
})

const filteredItems = ref(props.items);

watch(() => props.items, (newItems) => {
  filteredItems.value = newItems;
});

const debouncedFilter = (event) => {
  const query = event.target.value;
  if (query === '') {
    filteredItems.value = props.items;
    return;
  }
  
  if (isGrouped.value) {
    filteredItems.value = (props.items as SelectGroup[])
      .map(group => {
        const filteredOptions = group.options.filter(option =>
          option.label.toLowerCase().includes(query.toLowerCase())
        );
        return { ...group, options: filteredOptions };
      })
      .filter(group => group.options.length > 0);
  } else {
    filteredItems.value = (props.items as SelectItem[]).filter(item =>
      item.label.toLowerCase().includes(query.toLowerCase())
    );
  }
};

const selectedItem = computed({
  get: () => props.modelValue,
  set: (value: SelectItem | null) => {
    emit('update:modelValue', value)
  }
})

watch(selectedItem, (newValue) => {
  if (newValue) {
    nextTick(() => {
      // showOptions.value = false;
    });
  }
});
</script>